/**
 * Recorte do Painel de Assistências v2 — estado dos filtros da tela e sua
 * tradução para a query do backend. Puro (sem React, sem request).
 *
 * Convenção dos filtros dimensionais (igual ao FiltroExcel): `null` = sem
 * filtro; `Set` = seleção explícita. Set VAZIO não vira parâmetro nenhum —
 * "nada selecionado" é tratado na tela (não há o que pedir ao servidor).
 */
import { formatarReaisBR } from "../../../utils/dinheiro";

export const PERIODOS = [
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "180d", label: "6m" },
  { value: "365d", label: "1a" },
  { value: "mes", label: "Este mês" },
  { value: "mes_anterior", label: "Mês anterior" },
  { value: "ano", label: "Este ano" },
];

export const DATAS_BASE = [
  { value: "emissao", label: "Emissão", hint: "quando a solicitação foi criada" },
  { value: "autorizacao", label: "Autorização", hint: "quando foi autorizada" },
  { value: "expedicao", label: "Expedição", hint: "quando a retirada completa foi assinada" },
  { value: "atendimento", label: "Atendimento", hint: "quando foi marcada como atendida" },
];
export const DATA_BASE_LABEL = Object.fromEntries(DATAS_BASE.map((d) => [d.value, d.label]));

// Dimensões filtráveis (chave = parâmetro do backend = dimensão de /opcoes).
// `principal`: aparece sempre; as demais ficam atrás de "Mais filtros".
export const DIMENSOES = [
  { chave: "situacao", rotulo: "Situação", principal: true },
  { chave: "cliente", rotulo: "Cliente", principal: true },
  { chave: "processo", rotulo: "Processo de origem", principal: true },
  { chave: "nc", rotulo: "Não conformidade", principal: true },
  { chave: "solicitante", rotulo: "Solicitante" },
  { chave: "lider", rotulo: "Líder" },
  { chave: "supervisor", rotulo: "Supervisor" },
  { chave: "num_pedido", rotulo: "Pedido" },
  { chave: "ambiente", rotulo: "Ambiente" },
  { chave: "material", rotulo: "Material" },
  { chave: "causa", rotulo: "Causa-raiz" },
  { chave: "demanda", rotulo: "Demanda" },
  { chave: "imputacao", rotulo: "Imputação" },
  { chave: "rota", rotulo: "Rota" },
  { chave: "estado_prazo", rotulo: "Situação do prazo" },
];

export function recorteInicial() {
  return {
    periodo: "90d",
    de: "",
    ate: "",
    data_base: "emissao",
    tipos: null, // null = todos do escopo
    filtros: {}, // { [chave]: Set }
    rotulos: {}, // { "dimensao:valor": rótulo } — só para exibir
  };
}

/** Estado da tela → objeto de query (`montarQueryPainel` serializa). */
export function paraQuery(estado) {
  const q = { data_base: estado.data_base };
  if (estado.de || estado.ate) {
    q.de = estado.de || estado.ate;
    q.ate = estado.ate || estado.de;
  } else {
    q.periodo = estado.periodo || "90d";
  }
  if (estado.tipos && estado.tipos.size > 0) q.tipo = [...estado.tipos].sort();
  Object.entries(estado.filtros || {}).forEach(([chave, sel]) => {
    if (sel && sel.size > 0) q[chave] = [...sel];
  });
  return q;
}

/** Chave estável do recorte (cache de respostas e dependência de efeito). */
export function chaveDoRecorte(estado) {
  return JSON.stringify(paraQuery(estado));
}

/** Alguma dimensão está com "nada selecionado"? Aí o resultado é vazio por
 * definição e a tela avisa em vez de consultar. */
export function temSelecaoVazia(estado) {
  if (estado.tipos && estado.tipos.size === 0) return true;
  return Object.values(estado.filtros || {}).some((sel) => sel && sel.size === 0);
}

export function filtrosAtivos(estado) {
  return DIMENSOES
    .filter((d) => estado.filtros?.[d.chave])
    .map((d) => ({ ...d, quantidade: estado.filtros[d.chave].size }));
}

/** "01/07/2026 a 18/09/2026" a partir do `recorte` que o backend devolve. */
export function descreverPeriodo(recorte) {
  if (!recorte?.de || !recorte?.ate) return "";
  const br = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  };
  return recorte.de === recorte.ate ? br(recorte.de) : `${br(recorte.de)} a ${br(recorte.ate)}`;
}

// ---------- formatação de números do painel ----------

export function pct(v, casas = 1) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: casas })}%`;
}

export function du(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} d.u.`;
}

// Dinheiro sai do helper canônico (ADR-0059: um formatador só). Vazio vira
// "—" aqui porque no painel "sem estimativa" não é "R$ 0,00".
export function reais(v) {
  if (v === null || v === undefined || v === "") return "—";
  return formatarReaisBR(v);
}

export function inteiro(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString("pt-BR");
}
